import { useCallback, useEffect, useMemo } from 'react'
import { Loading } from '../ui/Loading'
import { useTheme } from '@/contexts/SettingsContext'
import { usePlaybookData } from '@/hooks/UsePlaybookData'
import { useConceptData } from '@/hooks/UseConceptData'
import { useModalManager } from '@/hooks/UseModalManager'
import { PlaybookEditorHeader } from './PlaybookEditorHeader'
import { PlaybookEditorDialogs } from './PlaybookEditorDialogs'
import { PlaybookEditorTabs } from './PlaybookEditorTabs'
import {
	usePlaybookEditorState,
	usePlaySelection,
	usePlaybookActions,
	usePlaybookConfirmations,
} from './hooks'
import { DEFAULT_PLAYBOOK_NAME } from './constants/Playbook'
import './playbook-editor.css'

const DARK_MODE_CLASS = 'dark'

interface PlaybookEditorProps {
	playbookId?: string
	playbookName?: string
	teamId?: string
	teamName?: string
	onBack?: () => void
	onOpenPlay?: (playId: string) => void
	onImport?: () => void
	onExport?: (selectedPlayIds?: string[]) => void
}

function PlaybookEditorContent({
	playbookId,
	playbookName = DEFAULT_PLAYBOOK_NAME,
	teamId,
	teamName,
	onBack,
	onOpenPlay,
	onImport,
}: PlaybookEditorProps) {
	const { theme } = useTheme()
	const { state, actions: stateActions } = usePlaybookEditorState()
	const { selectedPlays, togglePlaySelection, selectAllVisiblePlays, clearSelection, selectedCount } = usePlaySelection()

	const modals = useModalManager([
		'settings', 'share',
		'rename', 'export', 'playViewer',
	] as const)

	const playData = usePlaybookData(playbookId)
	const conceptData = useConceptData(playbookId)

	// Batch permanent delete handler - deletes all selected plays permanently
	const handlePermanentDeletePlays = useCallback(async (playIds: string[]) => {
		await Promise.allSettled(playIds.map((id) => playData.permanentDeletePlay(id)))
		clearSelection()
	}, [playData, clearSelection])

	const { deletePlayConfirm, permanentDeleteConfirm, deleteConceptConfirm } = usePlaybookConfirmations({
		onDeletePlay: playData.deletePlay,
		onPermanentDeletePlays: handlePermanentDeletePlays,
		onDeleteConcept: conceptData.deleteConcept,
		onDeleteFormation: conceptData.deleteFormation,
	})

	// Batch delete handler - soft deletes all selected plays to trash
	const handleBatchDelete = useCallback(async () => {
		const playIds = Array.from(selectedPlays)
		await Promise.allSettled(playIds.map((id) => playData.deletePlay(id)))
		clearSelection()
	}, [selectedPlays, playData, clearSelection])

	// Batch restore handler - restores all selected plays from trash
	const handleBatchRestore = useCallback(async () => {
		const playIds = Array.from(selectedPlays)
		await Promise.allSettled(playIds.map((id) => playData.restorePlay(id)))
		clearSelection()
	}, [selectedPlays, playData, clearSelection])

	// Batch permanent delete handler - prompts confirmation dialog
	const handleBatchPermanentDelete = useCallback(() => {
		const playIds = Array.from(selectedPlays)
		permanentDeleteConfirm.prompt(playIds)
	}, [selectedPlays, permanentDeleteConfirm])

	const playbookActions = usePlaybookActions({
		playbookId,
		plays: playData.plays,
		playData,
		stateActions,
		modals,
		deletePlayConfirm,
		deleteConceptConfirm,
		onOpenPlay,
		renamePlayId: state.renamePlayId,
		renamePlayName: state.renamePlayName,
	})

	// Wrap plays into exportSections for the export dialog
	const exportSections = useMemo(() => [{
		id: 'all',
		name: 'All Plays',
		plays: playData.plays,
	}], [playData.plays])

	// Theme effect
	useEffect(() => {
		document.documentElement.classList.toggle(DARK_MODE_CLASS, theme === DARK_MODE_CLASS)
	}, [theme])

	// Loading state
	if (playData.isLoading) {
		return <Loading fullscreen />
	}

	// Error state
	if (playData.error) {
		return (
			<div className="playbook-editor-error">
				<div className="playbook-editor-error-content">
					<h1 className="playbook-editor-error-title">Error</h1>
					<p className="playbook-editor-error-message">{playData.error}</p>
					<button onClick={onBack} className="playbook-editor-error-button">
						Back to Playbooks
					</button>
				</div>
			</div>
		)
	}

	return (
		<div className="playbook-editor">
			<div className="playbook-editor-main">
				<PlaybookEditorHeader
					playbookName={playbookName}
					teamName={teamName}
					searchQuery={state.searchQuery}
					viewMode={state.viewMode}
					selectedPlayCount={selectedCount}
					onBack={onBack}
					onSearchChange={stateActions.setSearchQuery}
					onViewModeChange={stateActions.setViewMode}
					onImport={() => onImport?.()}
					onExport={() => modals.open('export')}
					onShare={() => modals.open('share')}
					onSettings={() => modals.open('settings')}
				/>

				<PlaybookEditorTabs
					activeTab={state.activeTab}
					onTabChange={stateActions.setActiveTab}
					playbookId={playbookId!}
					plays={playData.plays}
					trashedPlays={playData.trashedPlays}
					viewMode={state.viewMode}
					searchQuery={state.searchQuery}
					selectedPlays={selectedPlays}
					isTrashView={state.isTrashView}
					onNewPlay={() => playbookActions.handleNewPlay()}
					onOpenPlay={playbookActions.handleOpenPlay}
					onAnimatePlay={playbookActions.handleAnimatePlay}
					onRenamePlay={playbookActions.handleRenamePlay}
					onDeletePlay={playbookActions.handleDeletePlay}
					onRestorePlay={playData.restorePlay}
					onDuplicatePlay={playbookActions.handleDuplicatePlay}
					onTogglePlaySelection={togglePlaySelection}
					onSelectAllVisible={() => selectAllVisiblePlays(playData.plays.map(p => p.id))}
					onTrashToggle={() => stateActions.setIsTrashView(!state.isTrashView)}
					onBatchDelete={handleBatchDelete}
					onBatchRestore={handleBatchRestore}
					onBatchPermanentDelete={handleBatchPermanentDelete}
					onClearSelection={clearSelection}
					onLoadMorePlays={playData.fetchNextPlays}
					hasMorePlays={playData.hasMorePlays}
					isLoadingPlays={playData.isFetchingPlays}
					formations={conceptData.formations}
					onNewFormation={playbookActions.handleNewFormation}
					onEditFormation={playbookActions.handleEditFormation}
					onDeleteFormation={(id) => playbookActions.handleDeleteConcept(id, 'formation')}
					onDuplicateFormation={async () => { /* TODO: wire formation duplication API */ }}
					onLoadMoreFormations={conceptData.fetchNextFormations}
					hasMoreFormations={conceptData.hasMoreFormations}
					isLoadingFormations={conceptData.isLoading}
					onDeleteConcept={(id) => playbookActions.handleDeleteConcept(id, 'concept')}
				/>
			</div>

			<PlaybookEditorDialogs
				playbookId={playbookId}
				teamId={teamId}
				playbookName={playbookName}
				modals={modals}
				exportSections={exportSections}
				allPlays={playbookActions.allPlays}
				renamePlayName={state.renamePlayName}
				viewingPlayId={state.viewingPlayId}
				onRenamePlayNameChange={stateActions.setRenamePlayName}
				onConfirmRename={playbookActions.confirmRename}
				deletePlayConfirm={deletePlayConfirm}
				permanentDeleteConfirm={permanentDeleteConfirm}
				deleteConceptConfirm={deleteConceptConfirm}
			/>
		</div>
	)
}

export default function PlaybookEditor(props: PlaybookEditorProps) {
	return <PlaybookEditorContent {...props} />
}
